import { MagpieConnection } from 'magpie-lib/connection';
import { useCallback, useMemo, useRef } from 'react';
import { getDeferred, getPortalId, waitForBoolean } from '../../common/utils';
import { ComponentState } from '../../consumer/models';
import {
  AnyComponentDefinition,
  InferDefinitionPropsType,
} from '../../platform/models/nav-component-definition';
import { navComponentCommsTopicDef } from '../../common/topics';
import { ncRemoteOperations } from '../../common/messages';
import {
  InferDefinitionFunctionArgs,
  InferDefinitionFunctionNames,
} from '../../platform/models';

export interface Props<CDef extends AnyComponentDefinition> {
  componentDef: CDef;
  debug?: boolean;
}

export interface UseNavigationComponentResult<
  CDef extends AnyComponentDefinition,
> {
  getProps: () => Promise<InferDefinitionPropsType<CDef>>;
  setProps: (props: Partial<InferDefinitionPropsType<CDef>>) => Promise<void>;
  getState: () => Promise<ComponentState>;
  show: () => Promise<void>;
  hide: () => Promise<void>;
  setIsLoaded: (value: boolean) => Promise<void>;
  invokeFunction: <
    FnName extends InferDefinitionFunctionNames<CDef> & string,
    Args extends InferDefinitionFunctionArgs<CDef, FnName>,
  >(
    fnName: FnName,
    args: Args
  ) => Promise<void>;
}

export function useNavigationComponent<CDef extends AnyComponentDefinition>({
  componentDef,
}: Props<CDef>): UseNavigationComponentResult<CDef> {
  const name = componentDef.getName();
  const platformName = componentDef.getPlatformName();

  try {
    MagpieConnection.get();
  } catch (e) {
    MagpieConnection.create({ portalId: getPortalId() });
  }

  const waitForMagpieConnection = useRef<Promise<boolean | null>>(
    waitForBoolean(() => !!MagpieConnection.get().getClientId())
  );

  const show: UseNavigationComponentResult<CDef>['show'] =
    useCallback(async () => {
      await waitForMagpieConnection.current;
      const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);
      topic.publish(
        ncRemoteOperations.setIsOpen.create({ name, value: true, platformName })
      );
    }, [name, platformName]);

  const hide: UseNavigationComponentResult<CDef>['hide'] =
    useCallback(async () => {
      await waitForMagpieConnection.current;
      const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);

      topic.publish(
        ncRemoteOperations.setIsOpen.create({
          name,
          value: false,
          platformName,
        })
      );
    }, [name, platformName]);

  const getState: UseNavigationComponentResult<CDef>['getState'] =
    useCallback(async () => {
      await waitForMagpieConnection.current;

      const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);
      const message = ncRemoteOperations.getState.create({
        name,
        platformName,
      });
      const { promise, resolve } = getDeferred<ComponentState>();

      topic.publish(message, {
        onReply: ({ payload: componentState }) => {
          resolve(componentState);
        },
      });

      return promise;
    }, [name, platformName]);

  const setIsLoaded: UseNavigationComponentResult<CDef>['setIsLoaded'] =
    useCallback(async () => {
      await waitForMagpieConnection.current;
      const { resolve, promise } = getDeferred<void>();
      const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);

      topic.publish(
        ncRemoteOperations.setIsLoaded.create({
          name,
          value: true,
          platformName,
        }),
        { onReply: () => resolve() }
      );
      return promise;
    }, [name, platformName]);

  const setProps: UseNavigationComponentResult<CDef>['setProps'] = useCallback(
    async (props: Partial<InferDefinitionPropsType<CDef>>) => {
      await waitForMagpieConnection.current;
      const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);

      topic.publish(
        ncRemoteOperations.setProps.create({ name, value: props, platformName })
      );
    },
    [name, platformName]
  );

  const getProps: UseNavigationComponentResult<CDef>['getProps'] =
    useCallback(async () => {
      await waitForMagpieConnection.current;
      const { resolve, promise } =
        getDeferred<InferDefinitionPropsType<CDef>>();
      const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);

      topic.publish(
        ncRemoteOperations.getProps.create({ name, platformName }),
        {
          onReply: ({ payload }) => resolve(payload as any),
        }
      );

      return promise;
    }, [name, platformName]);

  const invokeFunction: UseNavigationComponentResult<CDef>['invokeFunction'] =
    useCallback(
      async (fnName: string, args: any) => {
        await waitForMagpieConnection.current;
        const topic = MagpieConnection.get().forTopic(
          navComponentCommsTopicDef
        );
        const { resolve, promise } = getDeferred<void>();
        const message = ncRemoteOperations.invokeFunction.create({
          name,
          fnName,
          args,
          platformName,
        });
        topic.publish(message, { onReply: () => resolve() });
        return promise;
      },
      [name, platformName]
    );

  return useMemo(
    () => ({
      show,
      hide,
      getState,
      getProps,
      setProps,
      setIsLoaded,
      invokeFunction,
    }),
    [show, hide, getState, getProps, setProps, setIsLoaded, invokeFunction]
  );
}
